import React, { useEffect, useState } from "react";
import { Table, Button, Dropdown, InputGroup } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Field, Form as FORM, Formik } from "formik";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-multi-lang";
import Pagination from "../Helper/Pagination";
import NoDataFound from "../Helper/NoDataFound";
import { ButtonLoader } from "../Helper/Loader";
import {
  agentListStart,
} from "../../store/slices/AgentSlice";
import AgentFormModal from "./AgentFormModal";
import AgentViewModal from "./AgentViewModal";
import AgentInviteModal from "./AgentInviteModal";
import AgentDeleteModal from "./AgentDeleteModal";

const AgentIndex = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const t = useTranslation("agent");
  const profile = useSelector((state) => state.admin.profile);
  const agentList = useSelector((state) => state.agent.agentList);
  const agentCreate = useSelector((state) => state.agent.agentCreate);
  const agentUpdate = useSelector((state) => state.agent.agentUpdate);
  const agentInvite = useSelector((state) => state.agent.agentInvite);
  const agentDelete = useSelector((state) => state.agent.agentDelete);

  const [filter, setFilter] = useState({
    search_key: searchParams.get("search_key")
      ? searchParams.get("search_key")
      : "",
  });
  const [page, setPage] = useState(
    searchParams.get("page") ? Number(searchParams.get("page")) : 1
  );
  const [skipRender, setSkipRender] = useState(true);
  const [renderPage, setRenderPage] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [editAgent, setEditAgent] = useState(null);
  const [viewAgent, setViewAgent] = useState(null);
  const [inviteAgent, setInviteAgent] = useState(null);
  const [deleteAgent, setDeleteAgent] = useState(null);

  useEffect(() => {
    if (!profile.loading && Object.keys(profile.data).length > 0) {
      dispatch(
        agentListStart({
          ...filter,
          skip: 12 * (page - 1),
          take: 12,
        })
      );
    }
  }, [profile, filter, page, renderPage]);

  useEffect(() => {
    if (!skipRender) {
      setPage(searchParams.get("page") ? Number(searchParams.get("page")) : 1);
      setFilter((prev) => ({
        ...prev,
        search_key: searchParams.get("search_key")
          ? searchParams.get("search_key")
          : "",
      }));
    }
    setSkipRender(false);
  }, [searchParams]);

  useEffect(() => {
    if (
      createModal &&
      !agentCreate.loading &&
      Object.keys(agentCreate.data).length > 0
    ) {
      setCreateModal(false);
      setRenderPage((prev) => !prev);
    }
  }, [agentCreate]);

  useEffect(() => {
    if (
      editAgent &&
      !agentUpdate.loading &&
      Object.keys(agentUpdate.data).length > 0
    ) {
      setEditAgent(null);
      setRenderPage((prev) => !prev);
    }
  }, [agentUpdate]);

  useEffect(() => {
    if (
      inviteAgent &&
      !agentInvite.loading &&
      Object.keys(agentInvite.data).length > 0
    ) {
      setInviteAgent(null);
    }
  }, [agentInvite]);

  useEffect(() => {
    if (
      deleteAgent &&
      !agentDelete.loading &&
      Object.keys(agentDelete.data).length > 0
    ) {
      setDeleteAgent(null);
      setRenderPage((prev) => !prev);
    }
  }, [agentDelete]);

  const handlePageClick = ({ selected }) => {
    const params = searchParams.get("search_key")
      ? `search_key=${searchParams.get("search_key")}&`
      : "";
    navigate(`/agents?${params}page=${selected + 1}`);
  };

  const permissionsList = [
    "payin",
    "payout",
    "withdraw",
    "deposit",
    "payin_wallet",
    "payout_wallet",
  ];

  const renderPermissionBadges = (agent) => (
    <div className="d-flex flex-wrap gap-1">
      {permissionsList.map((key) => (
        <span
          key={`${agent.id}-${key}`}
          className={`badge ${
            agent?.configuration?.[key] ? "bg-success" : "bg-secondary"
          }`}
        >
          {t(`permissions.${key}`)}
        </span>
      ))}
    </div>
  );

  return (
    <>
      <div className="payment-link-sec">
        <div className="payment-link-box">
          <div className="payment-link-header-sec">
            <div className="create-payment-link-header-sec">
              <h3>
                {t("heading")}{" "}
                {!agentList.loading
                  ? `(${agentList.data?.total_agents || 0})`
                  : null}
              </h3>
            </div>
            <div className="transactions-list-filter-sec">
              <div className="header-search">
                <Formik
                  initialValues={{
                    search_key: searchParams.get("search_key")
                      ? searchParams.get("search_key")
                      : "",
                  }}
                  enableReinitialize
                  onSubmit={(val) => {
                    setFilter((prev) => ({
                      ...prev,
                      search_key: val.search_key,
                    }));
                    navigate(
                      val.search_key
                        ? `/agents?search_key=${val.search_key}`
                        : "/agents"
                    );
                  }}
                >
                  {({ setFieldValue }) => (
                    <FORM
                      className="form"
                      style={{ display: "flex", gap: "1em" }}
                    >
                      <InputGroup className="mb-0">
                        <Field
                          placeholder={t("search_placeholder")}
                          type="text"
                          className="form-control"
                          name="search_key"
                        />
                        {searchParams.get("search_key") && (
                          <InputGroup.Text id="basic-addon2">
                            <button
                              type="button"
                              className="search-btn"
                              onClick={() => {
                                setFieldValue("search_key", "");
                                setFilter((prev) => ({
                                  ...prev,
                                  search_key: "",
                                }));
                                navigate("/agents");
                              }}
                            >
                              <svg
                                height="20"
                                width="20"
                                viewBox="0 0 20 20"
                                aria-hidden="true"
                                focusable="false"
                                className="css-tj5bde-Svg"
                              >
                                <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path>
                              </svg>
                            </button>
                          </InputGroup.Text>
                        )}
                        <InputGroup.Text id="basic-addon2">
                          <button className="search-btn" type="submit">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              fill="#3E4351"
                              x="0"
                              y="0"
                              enableBackground="new 0 0 513.749 513.749"
                              version="1.1"
                              viewBox="0 0 513.749 513.749"
                              xmlSpace="preserve"
                            >
                              <path d="M504.352 459.061l-99.435-99.477c74.402-99.427 54.115-240.344-45.312-314.746S119.261-9.277 44.859 90.15-9.256 330.494 90.171 404.896c79.868 59.766 189.565 59.766 269.434 0l99.477 99.477c12.501 12.501 32.769 12.501 45.269 0 12.501-12.501 12.501-32.769 0-45.269l.001-.043zm-278.635-73.365c-88.366 0-160-71.634-160-160s71.634-160 160-160 160 71.634 160 160c-.094 88.326-71.673 159.906-160 160z"></path>
                            </svg>
                          </button>
                        </InputGroup.Text>
                      </InputGroup>
                    </FORM>
                  )}
                </Formik>
              </div>
              <Button
                type="button"
                className="snapgopay-payment-btn"
                onClick={() => setCreateModal(true)}
              >
                {t("add_agent")}
              </Button>
            </div>
          </div>
          <div className="recent-transaction-table-sec table-responsive">
            <React.Fragment>
              <Table>
                <thead>
                  <tr>
                    <th>{t("s_no")}</th>
                    <th>{t("name")}</th>
                    <th>{t("email")}</th>
                    <th>{t("permissions_heading")}</th>
                    <th>{t("created_at")}</th>
                    <th>{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {agentList.loading ? (
                    [...Array(5)].map((_, key) => (
                      <tr key={key}>
                        {[...Array(6)].map((__, idx) => (
                          <td key={idx}>
                            <Skeleton height={20} />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : agentList.data?.agents?.length > 0 ? (
                    agentList.data.agents.map((agent, i) => {
                      const isDeleting =
                        agentDelete.buttonDisable &&
                        agentDelete.data?.merchant_agent_id === agent.id;
                      return (
                        <tr key={agent.id || i}>
                          <td>{(page - 1) * 12 + (i + 1)}</td>
                          <td>{agent.name}</td>
                          <td>{agent.email}</td>
                          <td>{renderPermissionBadges(agent)}</td>
                          <td>{agent.created_at || "-"}</td>
                          <td>
                            <div className="payment-link-dropdown">
                              <Dropdown>
                                <div className="payment-link-icon-sec">
                                  <Dropdown.Toggle id="dropdown-basic">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="20"
                                      height="20"
                                      x="0"
                                      y="0"
                                      enableBackground="new 0 0 512 512"
                                      version="1.1"
                                      viewBox="0 0 512 512"
                                      xmlSpace="preserve"
                                    >
                                      <circle
                                        cx="458.667"
                                        cy="256"
                                        r="53.333"
                                      ></circle>
                                      <circle
                                        cx="256"
                                        cy="256"
                                        r="53.333"
                                      ></circle>
                                      <circle
                                        cx="53.333"
                                        cy="256"
                                        r="53.333"
                                      ></circle>
                                    </svg>
                                  </Dropdown.Toggle>
                                </div>
                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    onClick={() => setViewAgent(agent)}
                                  >
                                    <span>{t("view")}</span>
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => setEditAgent(agent)}
                                  >
                                    <span>{t("edit")}</span>
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => setInviteAgent(agent)}
                                    disabled={
                                      agentInvite.buttonDisable &&
                                      agentInvite.data?.merchant_agent_id ===
                                        agent.id
                                    }
                                  >
                                    {agentInvite.buttonDisable &&
                                    agentInvite.data?.merchant_agent_id ===
                                      agent.id ? (
                                      <ButtonLoader />
                                    ) : (
                                      <span>{t("send_invite")}</span>
                                    )}
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    disabled={agentDelete.buttonDisable}
                                    onClick={() => setDeleteAgent(agent)}
                                  >
                                    {isDeleting ? (
                                      <ButtonLoader />
                                    ) : (
                                      <span>{t("delete")}</span>
                                    )}
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        <NoDataFound />
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
              {!agentList.loading &&
              agentList.data?.agents &&
              agentList.data.agents.length > 0 ? (
                <div className="new-billings-pagination-sec">
                  <Pagination
                    page={page}
                    handlePageClick={handlePageClick}
                    pageCount={Math.ceil(
                      (agentList.data.total_agents || 0) / 12
                    )}
                  />
                </div>
              ) : null}
            </React.Fragment>
          </div>
        </div>
      </div>

      {createModal && (
        <AgentFormModal show={createModal} onHide={() => setCreateModal(false)} />
      )}
      {editAgent && (
        <AgentFormModal
          show={!!editAgent}
          onHide={() => setEditAgent(null)}
          agent={editAgent}
        />
      )}
      {viewAgent && (
        <AgentViewModal
          show={!!viewAgent}
          onHide={() => setViewAgent(null)}
          agent={viewAgent}
        />
      )}
      {inviteAgent && (
        <AgentInviteModal
          show={!!inviteAgent}
          onHide={() => setInviteAgent(null)}
          agent={inviteAgent}
        />
      )}
      {deleteAgent && (
        <AgentDeleteModal
          show={!!deleteAgent}
          onHide={() => setDeleteAgent(null)}
          agent={deleteAgent}
        />
      )}
    </>
  );
};

export default AgentIndex;
